import {useEffect, useState} from 'react'
import io from 'socket.io-client'
import {useAuthClient} from '../../../../utils/use-auth-client'
import {OOE_SERVICE_URL} from '../../../../utils/api-base'

// Joins the same socket.io room the legacy screens/payment-details.js uses
// for instant updates. screens/payment-information.js (the customer-facing
// checkout page served at the send-link shortened URL) emits 'nextStep' with
// the same uuid/paymentUuid, the server broadcasts 'stepTaken', and we merge
// that in as soon as it arrives.
// Socket.IO gives the rep immediate feedback, while the timeline endpoint is
// the durable source of truth after a refresh, reconnect, or async workflow
// completion. fetch-expiry is unrelated (one-off, not a poll) and stays as-is.
// Both endpoints are keyed by the SAME paymentUuid
// returned from cc/order/send-link (confirmed via cc-order-dao.js's
// fetchOrderByUuid, which fetch-expiry's orderUuid param and timeline's
// paymentUuid param both resolve through identically).
const POLL_INTERVAL_MS = 5000

export const TIMELINE_STEPS = [
  {step_number: 1, label: 'Payment Link Sent'},
  {step_number: 2, label: 'Link Opened'},
  {step_number: 3, label: 'Payment Method Added'},
  {step_number: 4, label: 'Order Confirmation'},
]

export function usePaymentLinkStatus(paymentUuid) {
  const client = useAuthClient()
  const [completedSteps, setCompletedSteps] = useState({})
  const [expiresAt, setExpiresAt] = useState(null)

  useEffect(() => {
    if (!paymentUuid) return
    let cancelled = false

    client(`cc/order/fetch-expiry/${paymentUuid}`)
      .then(response => {
        if (cancelled) return
        const configDetails = response?.data?.configDetails ?? []
        const updatedDate = response?.data?.updated_date
        const minutes = Number(configDetails?.[0]?.value)
        if (updatedDate && minutes) {
          setExpiresAt(new Date(updatedDate).getTime() + minutes * 60000)
        }
      })
      .catch(() => {})

    let pollTimer
    const pollTimeline = () => {
      client(`cc/order/timeline/${paymentUuid}`)
        .then(response => {
          if (cancelled) return
          const steps = response?.steps ?? []
          const persistedSteps = steps.reduce((acc, step) => {
            acc[step.step_number] = step.completed_at
            return acc
          }, {})
          setCompletedSteps(prev => ({...prev, ...persistedSteps}))

          if (persistedSteps[4]) clearInterval(pollTimer)
        })
        .catch(() => {})
    }

    pollTimeline()
    pollTimer = setInterval(pollTimeline, POLL_INTERVAL_MS)

    const socket = io(OOE_SERVICE_URL, {transports: ['websocket']})
    socket.on('connect', () => {
      socket.emit('join', {uuid: paymentUuid}, () => {})
    })
    socket.on('stepTaken', ({stepNumber}) => {
      if (cancelled) return
      setCompletedSteps(prev =>
        prev[stepNumber]
          ? prev
          : {...prev, [stepNumber]: new Date().toISOString()},
      )
    })

    return () => {
      cancelled = true
      clearInterval(pollTimer)
      socket.close()
    }
  }, [paymentUuid, client])

  const timeline = TIMELINE_STEPS.map(step => ({
    ...step,
    completedAt: completedSteps[step.step_number] ?? null,
  }))

  return {timeline, expiresAt}
}
